import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  Users,
  Copy,
  CheckCircle,
  TrendingUp,
  Upload,
  AlertCircle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ["#4f46e5", "#0ea5e9", "#16a34a", "#eab308", "#dc2626", "#8b5cf6", "#f97316", "#06b6d4"];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/dashboard/stats`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const kpiCards = [
    {
      title: "Total Leads",
      value: stats?.total_leads || 0,
      icon: Users,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: "Total Duplicates",
      value: stats?.total_duplicates || 0,
      icon: Copy,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
    },
    {
      title: "Unique Leads",
      value: stats?.unique_leads || 0,
      icon: CheckCircle,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      title: "Duplicate Rate",
      value: `${stats?.duplicate_percentage || 0}%`,
      icon: TrendingUp,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
  ];

  if (loading) {
    return (
      <Layout title="Dashboard" subtitle="Overview of your lead data">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="card-hover">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-2 mb-8">
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const duplicateData = [
    { name: "Internal", value: stats?.internal_duplicates || 0, color: "#f97316" },
    { name: "Historical", value: stats?.historical_duplicates || 0, color: "#dc2626" },
    { name: "Unique", value: stats?.unique_leads || 0, color: "#16a34a" },
  ];

  return (
    <Layout title="Dashboard" subtitle="Overview of your lead data">
      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8" data-testid="kpi-cards">
        {kpiCards.map((card, index) => (
          <Card key={index} className="card-hover border-border/60">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    {card.title}
                  </p>
                  <p className="text-3xl font-bold font-['Manrope'] tabular-nums" data-testid={`kpi-${card.title.toLowerCase().replace(/\s/g, '-')}`}>
                    {typeof card.value === "number" ? card.value.toLocaleString() : card.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${card.bgColor} flex items-center justify-center`}>
                  <card.icon className={`w-6 h-6 ${card.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid gap-4 lg:grid-cols-2 mb-8">
        {/* Leads by State */}
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Leads by State</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.leads_by_state?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.leads_by_state} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="state" type="category" width={50} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="count" fill="#4f46e5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No data available</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Duplicate Distribution */}
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Lead Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.total_leads > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={duplicateData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {duplicateData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No data available</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid gap-4 lg:grid-cols-2 mb-8">
        {/* Leads by Source */}
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Leads by Source</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.leads_by_source?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.leads_by_source}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="source" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No data available</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Uploads Trend */}
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Upload Trend (14 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.daily_uploads?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.daily_uploads}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="records" stroke="#4f46e5" strokeWidth={2} dot={{ fill: "#4f46e5" }} name="Records" />
                  <Line type="monotone" dataKey="uploads" stroke="#0ea5e9" strokeWidth={2} dot={{ fill: "#0ea5e9" }} name="Uploads" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No upload data yet</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Uploads */}
      <Card className="border-border/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Recent Uploads
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_uploads?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">File</th>
                    <th className="text-left py-3 px-4">Uploaded By</th>
                    <th className="text-right py-3 px-4">Total</th>
                    <th className="text-right py-3 px-4">Duplicates</th>
                    <th className="text-right py-3 px-4">Unique</th>
                    <th className="text-left py-3 px-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_uploads.map((upload) => (
                    <tr key={upload.upload_id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4 font-medium">{upload.file_name}</td>
                      <td className="py-3 px-4 text-muted-foreground">{upload.user_name}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{upload.total_records.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-orange-500">
                        {(upload.internal_duplicates + upload.historical_duplicates).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-green-500">
                        {upload.unique_records.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {new Date(upload.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <Upload className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No uploads yet. Start by uploading your first CSV file.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};

export default Dashboard;
